import { useState } from 'react';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { type IssuedValueMap } from '../../hooks/useIssuedData';
import { type ClientInfo } from '../../types';
import { useTranslation } from '../../i18n/i18n';
import DesktopLayout from '../../layouts/DesktopLayout';
import FaqModal, { type FaqTopic } from '../../components/FaqModal';
import NeedHelpSection from '../../components/NeedHelpSection';
import DesktopFlowFooter from '../../components/DesktopFlowFooter';
import LoadingState from '../../components/LoadingState';
import DataTableLayout from '../../components/DataTableLayout';
import DataTable from '../../components/DataTable';

type Props = {
  client?: ClientInfo;
  requestedDataGroups?: RequestedDataGroup[];
  requestedDataLoading?: boolean;
  issuedValueMap: IssuedValueMap;
  onAccept: () => void;
  onCancel: () => void;
  onBack?: () => void;
};

export default function DesktopIssuanceReviewPage({
  client,
  requestedDataGroups,
  requestedDataLoading,
  issuedValueMap,
  onAccept,
  onCancel,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const [faqTopic, setFaqTopic] = useState<FaqTopic | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const clientName = client?.name || t('dataReview.unknownClient');

  if (
    requestedDataLoading ||
    !requestedDataGroups ||
    requestedDataGroups.length === 0
  ) {
    return (
      <DesktopLayout>
        <LoadingState title={t('dataReview.loadingNames')} />
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="flex flex-1 flex-col items-center justify-center gap-12 w-full">
        <div className="flex flex-col items-center gap-3 text-center max-w-lg">
          <h2 className="text-2xl font-semibold text-foreground">
            {t('dataReview.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('dataReview.subtitleIssuance', { clientName })}
          </p>
        </div>

        <div className="w-full min-w-lg max-w-2xl">
          <DataTableLayout
            groups={requestedDataGroups}
            selectedIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
            issuedByKey="dataReview.issuedBy"
            variant="desktop"
          >
            {(activeGroup) => {
              const credentialValues = issuedValueMap.get(
                activeGroup.credentialUuid,
              );
              return (
                <DataTable
                  group={activeGroup}
                  columns={[
                    {
                      header: t('dataReview.attribute'),
                      render: (f) => f.name,
                    },
                    {
                      header: t('dataReview.value'),
                      render: (f) =>
                        credentialValues?.get(f.attributeUuid) ?? '-',
                    },
                  ]}
                />
              );
            }}
          </DataTableLayout>
        </div>

        <DesktopFlowFooter
          primaryLabel={t('dataReview.acceptReceive')}
          onPrimary={onAccept}
          secondaryLabel={onBack ? t('abort.goBack') : t('agentStart.cancel')}
          onSecondary={onBack ?? onCancel}
        />

        <NeedHelpSection
          topics={['howDataProtected', 'howObtainData']}
          onSelectTopic={setFaqTopic}
        />
        <FaqModal
          topic={faqTopic}
          onChangeTopic={setFaqTopic}
          onClose={() => setFaqTopic(null)}
        />
      </div>
    </DesktopLayout>
  );
}
